'use client'
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn, noop } from "../../utils/index.js";
import { ResponsiveWrapper } from "../ResponsiveWrapper/index.js";
import { expandableInputVariants } from "./ExpandableInput.variants.js";
import { DictationButton, ExpandableTextArea, SubmitButton, VoiceButton } from "./ExpandableInputComponents/index.js";
function ExpandableInput({ voiceButtonType = 'textButton', placeholder = 'Ask anything', id, value, onChange, maxRows = 3, disabled = false, dictationDisabled = false, showDictationButton = false, colorVariant = 'default', voiceButtonDisabled = false, onSubmitClickHandler, onVoiceClickHandler, onDictationClickHandler, onHeightChange, voiceButtonTooltipProps, slots = {
    wrapperSlot: '',
    contentSlot: '',
    textareaSlot: '',
    voiceButtonSlot: '',
    footerSlot: '',
    submitButtonSlot: '',
    dictationButtonSlot: ''
} }) {
    const [inputValue, setInputValue] = useState(value ?? '');
    const [isMultiLine, setIsMultiLine] = useState(false);
    const [minHeight, setMinHeight] = useState(void 0);
    const textareaRef = useRef(null);
    const maxHeightRef = useRef(0);
    const contentHeightRef = useRef(0);
    useEffect(()=>{
        if (void 0 !== value) setInputValue(value);
    }, [
        value
    ]);
    useEffect(()=>{
        const textarea = textareaRef.current;
        if (textarea) {
            const MAX_HEIGHT = (minHeight ?? 0) * maxRows;
            textarea.style.height = 'auto';
            const rafId = globalThis.requestAnimationFrame(()=>{
                let contentHeight = textarea.scrollHeight;
                const hasMultipleLines = contentHeight > (minHeight ?? 0);
                if (hasMultipleLines) setIsMultiLine(true);
                if (!inputValue) setIsMultiLine(false);
                contentHeight = Math.max(contentHeight, minHeight ?? 0);
                if (MAX_HEIGHT > 0) contentHeight = Math.min(contentHeight, MAX_HEIGHT);
                if (maxHeightRef.current !== MAX_HEIGHT) maxHeightRef.current = MAX_HEIGHT;
                textarea.style.height = `${contentHeight}px`;
                contentHeightRef.current = contentHeight;
                onHeightChange?.(contentHeight);
            });
            return ()=>{
                globalThis.cancelAnimationFrame(rafId);
            };
        }
    }, [
        inputValue,
        maxRows,
        onHeightChange,
        minHeight,
        isMultiLine
    ]);
    const { wrapperSlot, contentSlot, textareaSlot, voiceButtonSlot, submitButtonSlot, dictationButtonSlot, footerSlot } = expandableInputVariants({
        isMultiLine,
        disabled,
        colorVariant
    });
    const wrapperClasses = cn(wrapperSlot(), slots.wrapperSlot);
    const contentClasses = cn(contentSlot(), slots.contentSlot);
    const textareaClasses = cn(textareaSlot(), slots.textareaSlot);
    const voiceButtonClasses = cn(voiceButtonSlot(), slots.voiceButtonSlot);
    const submitButtonClasses = cn(submitButtonSlot(), slots.submitButtonSlot);
    const dictationButtonClasses = cn(dictationButtonSlot(), slots.dictationButtonSlot);
    const footerClasses = cn(footerSlot(), slots.footerSlot);
    const handleChange = useCallback((e)=>{
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange?.(newValue);
    }, [
        onChange
    ]);
    const handleSubmit = useCallback(()=>{
        if (!onSubmitClickHandler || !inputValue.trim()) return;
        onSubmitClickHandler(inputValue);
        setInputValue('');
        onChange?.('');
    }, [
        onSubmitClickHandler,
        inputValue,
        onChange
    ]);
    const handleKeyDown = useCallback((e)=>{
        if ('Enter' === e.key && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    }, [
        handleSubmit
    ]);
    const renderDictationButton = useCallback(()=>/*#__PURE__*/ jsx(DictationButton, {
            disabled: disabled,
            dictationDisabled: dictationDisabled,
            onDictationClickHandler: onDictationClickHandler ?? noop,
            slots: {
                iconSlot: dictationButtonClasses
            }
        }), [
        disabled,
        dictationDisabled,
        dictationButtonClasses,
        onDictationClickHandler
    ]);
    const renderButtons = useCallback(()=>{
        if (0 === inputValue.length) return /*#__PURE__*/ jsx(VoiceButton, {
            onVoiceClickHandler: onVoiceClickHandler,
            voiceButtonType: voiceButtonType,
            slots: {
                iconSlot: voiceButtonClasses
            },
            tooltipProps: voiceButtonTooltipProps,
            disabled: voiceButtonDisabled,
            glass: 'glass' === colorVariant
        });
        return /*#__PURE__*/ jsx(SubmitButton, {
            handleSubmit: handleSubmit,
            disabled: disabled,
            slots: {
                buttonSlot: submitButtonClasses
            },
            glass: 'glass' === colorVariant
        });
    }, [
        inputValue.length,
        voiceButtonClasses,
        submitButtonClasses,
        voiceButtonDisabled,
        onVoiceClickHandler,
        voiceButtonType,
        voiceButtonTooltipProps,
        disabled,
        colorVariant,
        handleSubmit
    ]);
    return /*#__PURE__*/ jsx(ResponsiveWrapper, {
        children: /*#__PURE__*/ jsx("div", {
            className: wrapperClasses,
            children: /*#__PURE__*/ jsxs("div", {
                className: contentClasses,
                children: [
                    /*#__PURE__*/ jsx(ExpandableTextArea, {
                        textareaRef: textareaRef,
                        setMinHeight: setMinHeight,
                        id: id ?? '',
                        inputValue: inputValue,
                        placeholder: placeholder || 'Message input',
                        handleChange: handleChange,
                        handleKeyDown: handleKeyDown,
                        disabled: disabled,
                        slots: {
                            textareaSlot: textareaClasses
                        }
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: footerClasses,
                        children: [
                            showDictationButton && renderDictationButton(),
                            renderButtons()
                        ]
                    })
                ]
            })
        })
    });
}
export { ExpandableInput };
